<template>
    <div class="kyt-loading-container" :class="{ 'overlay': overlay }">
        <div class="kyt-loading-content">
            <div class="spinner-border" :class="spinnerClass" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
            <p v-if="message" class="loading-message mt-3 mb-0">{{ message }}</p>
        </div>
    </div>
</template>

<script setup lang="ts">
interface Props {
    size?: 'sm' | 'md' | 'lg';
    color?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark';
    message?: string;
    overlay?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    size: 'md',
    color: 'primary',
    overlay: false
});

const spinnerClass = computed(() => {
    const classes = [`text-${props.color}`];
    
    if (props.size === 'sm') {
        classes.push('spinner-border-sm');
    }
    
    return classes.join(' ');
});
</script>

<script lang="ts">
import { computed } from 'vue';

export default {
    name: 'KytChainLoading'
};
</script>

<style scoped>
.kyt-loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    min-height: 200px;
}

.kyt-loading-container.overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.9);
    z-index: 9999;
    min-height: 100vh;
}

.kyt-loading-content {
    text-align: center;
}

.loading-message {
    color: #6c757d;
    font-size: 0.875rem;
    font-weight: 500;
}

.spinner-border {
    width: 3rem;
    height: 3rem;
    border-width: 0.3em;
}

.spinner-border-sm {
    width: 1.5rem;
    height: 1.5rem;
    border-width: 0.2em;
}

/* Animation improvements */
.spinner-border {
    animation: spinner-border 0.75s linear infinite;
}

@keyframes spinner-border {
    to {
        transform: rotate(360deg);
    }
}

/* Color variants */
.text-primary {
    color: #007bff !important;
    border-color: #007bff transparent #007bff transparent;
}

.text-secondary {
    color: #6c757d !important;
    border-color: #6c757d transparent #6c757d transparent;
}

.text-success {
    color: #28a745 !important;
    border-color: #28a745 transparent #28a745 transparent;
}

.text-danger {
    color: #dc3545 !important;
    border-color: #dc3545 transparent #dc3545 transparent;
}

.text-warning {
    color: #ffc107 !important;
    border-color: #ffc107 transparent #ffc107 transparent;
}

.text-info {
    color: #17a2b8 !important;
    border-color: #17a2b8 transparent #17a2b8 transparent;
}

.text-light {
    color: #f8f9fa !important;
    border-color: #f8f9fa transparent #f8f9fa transparent;
}

.text-dark {
    color: #343a40 !important;
    border-color: #343a40 transparent #343a40 transparent;
}
</style>
